import { useCallback, useMemo } from 'react';
import { usePermissions } from '../../login/PermissionContext';
import { INV_ACTIONS, MODULE_GATES, normalizePermissionCode } from '../utils/inventoryRoles';
import { invHomePath } from '../navigation';
import { resolveAssignedStoreIds } from '../utils/assignedStores';

/**
 * The current user's inventory permissions — see `rbac spec.md` and the catalogues
 * PERMISSIONS_INVENTORY.md / PERMISSIONS_MATERIAL.md.
 *
 * The name is historical: this no longer resolves a *role*. Policy comes from the
 * backend as a list of granted permission codes; roles exist only there.
 *
 * Source is `get_logged_in_user_info/` via PermissionContext. Two properties of that
 * payload drive everything here:
 *   1. `role_permission` is an ARRAY — a user may hold several roles, so permissions
 *      are OR-unioned across every entry. `true` wins; `is_accessible: false` means
 *      "this role does not grant it", never "deny".
 *   2. Every permission is listed with a flag, so PRESENCE IS NOT GRANT — the
 *      `is_accessible === true` test below is load-bearing. Dropping it grants
 *      everything to everyone.
 *
 * Note PermissionContext.js only reads `role_permission[0].permissions` for the
 * landing page and RestrictedRoute. The suite is the first module to union the whole
 * array, so with multiple roles it can legitimately disagree with them.
 */

/** Union of every granted code across every role entry. */
function buildGrantedSet(userInfo) {
  const granted = new Set();
  const roles = Array.isArray(userInfo?.role_permission) ? userInfo.role_permission : [];

  roles.forEach((entry) => {
    const list = Array.isArray(entry?.permissions) ? entry.permissions : [];
    list.forEach((permission) => {
      if (permission?.is_accessible !== true) return;
      const code = normalizePermissionCode(permission.permission_name);
      if (code) granted.add(code);
    });
  });

  return granted;
}

/**
 * A mismatch between our codes and the seeded ones fails closed and silent — the
 * button simply is not there. This turns that into one console line per session.
 */
function warnOnCodeDrift(granted) {
  if (process.env.NODE_ENV === 'production' || !granted.size) return;
  const ours = Object.values(INV_ACTIONS);
  const missing = ours.filter(code => !granted.has(code));
  if (missing.length === ours.length) {
    console.warn('[inventory] no inventory permissions granted — role not seeded?');
  } else if (missing.length) {
    console.warn('[inventory] permissions not granted for this user:', missing);
  }
}

export default function useInvRole() {
  const { userInfo, loading } = usePermissions();

  const granted = useMemo(() => {
    const set = buildGrantedSet(userInfo);
    warnOnCodeDrift(set);
    return set;
  }, [userInfo]);

  // Must stay useCallback-wrapped: several DataGrid `columns` memos list `can` as a
  // dependency, and an inline arrow would rebuild them on every render.
  const can = useCallback(
    (action) => !loading && Boolean(action) && granted.has(action),
    [loading, granted],
  );

  const modules = useMemo(() => ({
    stock: !loading && granted.has(MODULE_GATES.stock),
    material: !loading && granted.has(MODULE_GATES.material),
    pharmacy: !loading && granted.has(MODULE_GATES.pharmacy),
  }), [loading, granted]);

  const homePath = useMemo(() => invHomePath(modules, can), [modules, can]);

  /** Assigned stores from login / get_logged_in_user_info. Empty = unscoped (all stores). */
  const myStoreIds = useMemo(
    () => resolveAssignedStoreIds(userInfo?.inventory_details),
    [userInfo],
  );

  /** First assigned store — fallback only. Prefer myStoreIds / useActiveStore. */
  const myStoreId = myStoreIds[0] || null;

  return { loading, can, modules, myStoreId, myStoreIds, homePath };
}
